// src/utils/sessionUtils.js
// Utility functions for session management

/**
 * Clear all session data including localStorage, sessionStorage, and cookies
 */
export const clearAllSessionData = () => {
  try {
    // Clear localStorage
    localStorage.clear()
    
    // Clear sessionStorage
    sessionStorage.clear()
    
    // Clear all cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    })
    
    // Clear specific auth-related cookies
    const cookiesToClear = [
      'access_token',
      'refresh_token', 
      'user_data',
      'device_id',
      'last_login',
      'demo_mode',
      'intended_route',
      'lang',
      'theme',
      'preferences',
      'session_id',
      'csrf_token'
    ]
    
    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`
    })
    
    return true
  } catch (error) {
    console.error("Error clearing session data:", error)
    return false
  }
}

/**
 * Clear only authentication-related data
 */
export const clearAuthData = () => {
  try {
    // Clear auth-related localStorage items
    const authKeys = [
      'access_token',
      'refresh_token',
      'user_data',
      'device_id',
      'last_login',
      'demo_mode',
      'intended_route'
    ]
    
    authKeys.forEach(key => {
      localStorage.removeItem(key)
    })
    
    // Clear auth-related cookies
    const authCookies = [
      'access_token',
      'refresh_token',
      'user_data',
      'device_id',
      'last_login',
      'demo_mode',
      'intended_route'
    ]
    
    authCookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`
    })
    
    return true
  } catch (error) {
    console.error("Error clearing auth data:", error)
    return false
  }
}

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('access_token')
  const userData = localStorage.getItem('user_data')
  return !!(token && userData)
}

/**
 * Get current user data
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('user_data')
    return userData ? JSON.parse(userData) : null
  } catch (error) {
    console.error('Error parsing user data:', error)
    return null
  }
}

/**
 * Force logout and redirect to login page
 */
export const forceLogout = () => {
  clearAllSessionData()
  window.location.replace("/auth/login")
}

/**
 * Logout with API call and then clear session
 */
export const logoutWithAPI = async (logoutAPI) => {
  try {
    // Attempt API logout
    await logoutAPI()
  } catch (error) {
    console.error("API logout failed:", error)
  } finally {
    // Clear session data regardless of API response
    clearAllSessionData()
    window.location.replace("/auth/login")
  }
}

// Make functions available globally for testing
if (typeof window !== 'undefined') {
  window.clearAllSessions = clearAllSessionData
  window.clearAuthData = clearAuthData
  window.forceLogout = forceLogout
  window.isAuthenticated = isAuthenticated
  window.getCurrentUser = getCurrentUser
}
